import React, { useCallback, useEffect, useRef } from "react"
import {
  NavigationProp,
  Route,
  useFocusEffect,
  useRoute,
} from "@react-navigation/native"
import { useDispatch, useSelector } from "react-redux"
import { GlobalState } from "@redux"
import { EstablishmentSelection } from "./EstablishmentSelection"
import { BadgeForm } from "./BadgeForm"
import { SecurityForm } from "./SecurityForm"
import { CashSystem, HoldingView } from "@foodi/core"
import { View, ScrollView, StyleSheet } from "react-native"
import { useState } from "react"
import { SuccessLanding } from "./SuccessLanding"
import { BadgeAssociationTemplate } from "@atomic"
import useBadgeManagement from "@hooks/useBadgeManagement"
import { AccountActions } from "@modules/badge/ducks"
import { Colors, Spacing } from "@constants"
import { PreferredZoneField } from "./BadgeForm/PreferredZoneField"

type BadgeSetupProps = {
  navigation: NavigationProp<any>
  route?: Route<any>
}

export enum BadgeLayouts {
  EstablishmentSelection = "EstablishmentSelection",
  BadgeForm = "BadgeForm",
  SecurityForm = "SecurityForm",
  SuccessLanding = "SuccessLanding",
}

export const BadgeAssociation = ({ navigation }: BadgeSetupProps) => {
  const mainRef = useRef<ScrollView>(null)
  const badgeFormRef = useRef<View>(null)
  const securityFormRef = useRef<View>(null)
  const previouslyCenteredComponent = useRef<BadgeLayouts>()
  const [componentPositions, setComponentPositions] = useState<
    {
      [key in BadgeLayouts]: number
    }
  >({
    EstablishmentSelection: 0,
    BadgeForm: 0,
    SecurityForm: 0,
    SuccessLanding: 0,
  })

  const [preferredZoneData, setPreferredZoneData] = useState({
    selectedPrefZones: [] as any[],
    hasPrefZonesError: true,
  });
  const [holdingViewAlias, setHoldingViewAlias] = useState('');

  const { selectedHolding, hasBadge, successfullyAddedHolding } = useSelector(
    (state: GlobalState) => state.account
  )

  const route = useRoute()
  const { holdingView }: { holdingView?: HoldingView } = route.params || {}

  const dispatch = useDispatch()

  const { reset } = useBadgeManagement()

  const isNotCashSystemOthers =
    selectedHolding?.idCashSystem !== Number(CashSystem.OTHERS)
  const isCashSystemProtected = selectedHolding?.isProtected

  const isBadgeFormCondition =
    selectedHolding &&
    hasBadge === true &&
    isNotCashSystemOthers &&
    !successfullyAddedHolding

  const isSecurityFormCondition =
    selectedHolding &&
    hasBadge === false &&
    isNotCashSystemOthers &&
    isCashSystemProtected &&
    !successfullyAddedHolding

  const isEstablishmentSelectionCondition =
    !selectedHolding && !successfullyAddedHolding

  const scrollToComponent = (componentKey: BadgeLayouts) => {
    const position = componentPositions[componentKey]
    if (position !== undefined && mainRef.current) {
      mainRef.current.scrollTo({ y: position, animated: true })
    }
  }

  const handleLayout = (event: any, componentKey: BadgeLayouts) => {
    if (previouslyCenteredComponent.current === componentKey) {
      return
    }
    setComponentPositions((prev) => ({
      ...prev,
      [componentKey]: event.nativeEvent.layout.y,
    }))
    previouslyCenteredComponent.current = componentKey
  }

  useEffect(() => {
    setTimeout(() => {
      if (isBadgeFormCondition) {
        scrollToComponent(BadgeLayouts.BadgeForm)
      } else if (isSecurityFormCondition) {
        scrollToComponent(BadgeLayouts.SecurityForm)
      } else if (
        isEstablishmentSelectionCondition ||
        successfullyAddedHolding
      ) {
        mainRef.current?.scrollTo({ y: 0, animated: true })
      }
    }, 100)
  }, [isBadgeFormCondition, isSecurityFormCondition, componentPositions])

  useEffect(() => {
    if (holdingView && holdingView.holding) {
      dispatch(AccountActions.setIsEditionMode(true))
      dispatch(AccountActions.setEditionHoldingView(holdingView))
    }
  }, [holdingView, dispatch])

  useFocusEffect(
    useCallback(() => {
      return () => {
        reset()
      }
    }, [])
  )

  return (
    <BadgeAssociationTemplate ref={mainRef} navigation={navigation}>
      {successfullyAddedHolding ? (
        <SuccessLanding navigation={navigation} layoutHandler={handleLayout} />
      ) : (
        <EstablishmentSelection navigation={navigation} />
      )}
      {(!successfullyAddedHolding && 
        (isBadgeFormCondition || isSecurityFormCondition)
      ) && (
        <View style={styles.separator} />
      )}
      {(!successfullyAddedHolding && selectedHolding?.enabledPreferredZones) && (
        <>
          <PreferredZoneField
            zoneData={preferredZoneData}
            setZoneData={setPreferredZoneData}
          />
          <View style={styles.separator} />
        </>
      )}

      {isBadgeFormCondition && (
        <BadgeForm
          layoutHandler={handleLayout}
          ref={badgeFormRef}
          mainRef={mainRef}
          navigation={navigation}
          preferredZoneData={preferredZoneData}
          setPreferredZoneData={setPreferredZoneData}
          holdingViewAlias={holdingViewAlias}
          setHoldingViewAlias={setHoldingViewAlias}
        />
      )}
      {isSecurityFormCondition && (
        <SecurityForm
          layoutHandler={handleLayout}
          ref={securityFormRef}
          navigation={navigation}
          preferredZoneData={preferredZoneData}
          setPreferredZoneData={setPreferredZoneData}
          holdingViewAlias={holdingViewAlias}
        />
      )}
    </BadgeAssociationTemplate>
  )
}

const styles = StyleSheet.create({
  separator: {
    width: "90%",
    height: 2,
    backgroundColor: Colors.tagsGray,
    borderRadius: 2,
    marginVertical: Spacing.XL,
  }
})